import React from 'react'
import {
    IconChat,
    IconConfetti,
    IconCursorClick,
    IconDatabase,
    IconEye,
    IconInfo,
    IconList,
    IconMagic,
    IconMessage,
    IconRocket,
    IconSparkles,
} from '@posthog/icons'
import Link from 'components/Link'
import { getTool } from '../../data/tools'
import { applications, GetAccess, keyFeatures } from './data_warehouse/slides'

// this data powers the data warehouse page, but the product icons that appear on /products and in the menu bar are defined in productNavigation.ts.

const YES = true
const NO = false

export const dataWarehouse = {
    ...getTool('data_warehouse'),
    Icon: IconDatabase,
    type: 'data_warehouse',
    categoryName: 'Sources',
    color: 'purple',
    colorSecondary: 'lilac',
    // The product lives at /warehouse, but its docs live at /docs/data-warehouse.
    docsSlug: 'data-warehouse',
    forumTag: 'data-warehouse',
    teamSlug: 'warehouse',
    // The Learn tab shows only the "Ask a quick question" part of the Data Stack pocket guide.
    pocketGuideVolume: 'data-stack',
    pocketGuideSection: 'Ask a quick question',
    // Each of those chapters is a question, so the full title says more than the short label.
    learnNavFullTitles: true,
    seo: {
        title: 'Warehouse – All your data, one warehouse',
        description:
            'A fully managed warehouse next to your PostHog data. Sync Stripe, your CRM, and Postgres, then query everything with SQL, plain English, or your agent.',
    },
    overview: {
        title: 'All your data, one warehouse',
        description: '"Show me which features paying customers use the most"',
        eli5: (
            <>
                <p>
                    PostHog already syncs Stripe, HubSpot, Postgres, and the rest of your stack, so you can query it
                    next to your events. The Warehouse goes one step further: it's an actual database, with its own
                    hostname, credentials, and storage, holding your product data and business data side by side.
                </p>
                <p>
                    Point Metabase, a notebook, or a scheduled job at it, build your own tables in it, and run heavy
                    queries without slowing down PostHog. It's the warehouse you'd otherwise set up in Snowflake or
                    BigQuery, without the pipeline to get your product data into it.
                </p>
                <p>
                    PostHog’s AI features can use it as context, so asking "why did conversion drop?" comes back with
                    the revenue impact, the cohorts affected, and what those users have in common.
                </p>
            </>
        ),
        textColor: 'text-white', // tw
    },
    // The warehouse is a sales-led beta, so the hero sends people to sales instead of signup.
    ctaButtons: {
        primary: { label: 'Contact sales', to: '/talk-to-a-human' },
        secondary: { label: 'Read the docs', to: '/docs/data-warehouse/warehouse' },
    },
    productMenu: [
        { slug: 'overview', name: 'Overview', icon: <IconEye className="size-4" /> },
        {
            slug: 'eli5',
            name: 'What does it do?',
            hideFromNav: true,
            group: 'divided',
            icon: <IconInfo className="size-4" />,
        },
        {
            slug: 'use-cases',
            name: 'Who is it for?',
            hideFromNav: true,
            group: 'divided',
            icon: <IconMagic className="size-4" />,
        },
        {
            slug: 'applications',
            name: 'How do I use it?',
            group: 'divided',
            icon: <IconCursorClick className="size-4" />,
            props: { slides: applications },
        },
        {
            slug: 'key-features',
            name: 'Key features',
            group: 'divided',
            icon: <IconSparkles className="size-4" />,
            props: { slides: keyFeatures },
        },
        { slug: 'ask-anything', name: 'AI prompts', group: 'divided', icon: <IconChat className="size-4" /> },
        { slug: 'pairs-with', name: 'Pairs with...', hideFromNav: true, icon: <IconConfetti className="size-4" /> },
        {
            slug: 'feature-comparison',
            name: 'Feature comparison',
            group: 'divided',
            icon: <IconList className="size-4" />,
        },
        { slug: 'community', name: 'Questions?', group: 'divided', icon: <IconMessage className="size-4" /> },
        {
            slug: 'get-access',
            name: 'Get access',
            group: 'divided',
            icon: <IconRocket className="size-4" />,
            component: GetAccess,
        },
    ],
    hogs: {
        default: {
            src: 'https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/contents/images/products/data-warehouse/warehouse-hog.png',
            alt: 'A hedgehog tending the warehouse',
        },
        mobileHog: {
            src: 'https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/contents/images/products/data-warehouse/warehouse-hog.png',
            alt: 'A hedgehog tending the warehouse',
        },
    },
    useCases: {
        rows: [
            [
                'Product Engineers',
                'Join product events to production tables without building a pipeline, and answer "who\'s actually using this?" in one query',
            ],
            [
                'Data Engineers',
                'Get Stripe, CRM, and Postgres data landed and kept in sync with incremental syncs and Postgres CDC',
            ],
            [
                'PMs',
                'See which features correlate with expansion and churn, and which experiment wins turned into revenue',
            ],
            [
                'Finance & RevOps',
                'Break MRR, churn, and expansion down by product behavior, plan, and acquisition channel',
            ],
            [
                'Growth & Marketing',
                'Follow a signup from first touch to CRM deal stage to paid, and see where the funnel leaks',
            ],
        ],
    },
    ai: {
        intro: 'Ask PostHog AI any question you want about your data.',
        copyPrompts: true,
        groups: [
            {
                title: 'Revenue',
                prompts: [
                    'Which features do our highest-MRR customers use most?',
                    'Which behaviors in the first 14 days predict a customer upgrading?',
                ],
            },
            {
                title: 'Experiments',
                prompts: [
                    "Did the winning variant of last month's pricing experiment increase revenue?",
                    'Why does this variant perform better in Europe than in the US?',
                ],
            },
            {
                title: 'Retention',
                prompts: [
                    'Did the users who completed onboarding in September still have an active subscription 60 days later?',
                    'Which accounts have usage dropping and a renewal in the next 30 days?',
                ],
            },
            {
                title: 'Funnels & CRM',
                prompts: [
                    'Where does the funnel from signup to closed-won deal lose the most people?',
                    'Which HubSpot lead sources turn into accounts that stick around?',
                ],
            },
        ],
    },
    /**
     * Honest comparison against mature cloud warehouses. Every cell was checked against
     * vendor docs in October 2026 – re-check before changing a value.
     * Values are in column order: PostHog, BigQuery, Snowflake, Redshift.
     */
    comparison: {
        companies: [
            { name: 'PostHog', key: 'posthog' },
            { name: 'BigQuery', key: 'bigquery' },
            { name: 'Snowflake', key: 'snowflake' },
            { name: 'Redshift', key: 'redshift' },
        ],
        rows: [
            { label: 'Setup', description: 'Getting data in and keeping it there' },
            {
                label: 'Product analytics data included',
                description: 'Events and persons land automatically',
                values: [YES, 'GA4 export', NO, NO],
            },
            {
                label: 'Events in near real time',
                description: 'New events land within minutes of capture',
                values: ['US Cloud', 'GA4 streaming export', NO, NO],
            },
            {
                label: 'Serverless compute',
                description: 'Compute starts on demand, with no cluster to manage',
                values: [YES, YES, 'You size warehouses', 'Serverless option'],
            },
            { label: 'Built-in connectors', description: 'Native sources, without a third-party ELT tool' },
            { label: 'Stripe', values: [YES, 'Preview', NO, NO] },
            { label: 'HubSpot', values: [YES, 'Preview', YES, NO] },
            { label: 'Salesforce', values: [YES, YES, YES, YES] },
            {
                label: 'Postgres CDC',
                description: 'Row-level changes in near real time',
                values: ['Alpha', YES, YES, YES],
            },
            { label: 'Querying', description: 'Asking questions of your data' },
            { label: 'SQL editor', values: [YES, YES, YES, YES] },
            {
                label: 'Plain-English questions',
                description: 'Ask in natural language, get SQL back',
                values: [YES, YES, YES, YES],
            },
            {
                label: 'Machine learning in the warehouse',
                description: 'Train or run models where the data lives',
                values: [NO, YES, YES, YES],
            },
            { label: 'In the product', description: 'Using warehouse data without writing SQL' },
            {
                label: 'Filter and break down insights by warehouse data',
                description: 'For example by Stripe plan or CRM deal stage',
                values: [YES, NO, NO, NO],
            },
            {
                label: 'Experiments measured on warehouse data',
                description: 'For example revenue from Stripe',
                values: [YES, NO, NO, NO],
            },
        ],
    },
    screenshots: {
        overview: {
            src: 'https://res.cloudinary.com/dmukukwp6/image/upload/dw_temp_528efa76a2.png',
            alt: 'Screenshot of the PostHog data warehouse',
            classes: '',
        },
        home: {
            src: 'https://res.cloudinary.com/dmukukwp6/image/upload/screenshot_data_warehouse_light_b0cdbebe8f.png',
            srcDark: 'https://res.cloudinary.com/dmukukwp6/image/upload/screenshot_data_warehouse_dark_8f465ecfaa.png',
            alt: 'Data Warehouse screenshot',
            classes: 'justify-center items-end px-4 @lg:px-6',
            imgClasses: 'rounded-t shadow-2xl',
        },
    },
    hog: {
        src: 'https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/contents/images/products/data-warehouse/warehouse-hog.png',
        alt: 'Just another hedgehog',
        classes: 'absolute bottom-0 right-0 max-w-[140px]',
    },
    slider: {
        marks: [1000000, 100000000, 1000000000],
        min: 1000000,
        max: 1000000000,
    },
    volume: 1000000,
    includeAddonRates: true,
    addonSliders: [
        {
            key: 'data_warehouse_historical',
            label: 'Historical syncs',
            sliderConfig: {
                marks: [1000000, 1000000000, 10000000000],
                min: 1000000,
                max: 10000000000,
            },
            volume: 1000000,
            unit: 'row',
            freeAllocationText: 'Free for the first 7 days of each new source (100M on free plan, unlimited on paid)',
        },
    ],
    customers: {
        headshotpro: {
            headline: 'analyzes Google Adwords data in PostHog',
            description: 'I needed one source of truth for both marketing and developers to share.',
        },
        webshare: {
            headline: 'syncs Intercom messages and customer contacts',
            description: "PostHog is the single source of truth for us. We've put everything in one place.",
        },
    },
    features: [
        {
            title: 'Query with PostHog SQL',
            headline: 'Query with PostHog SQL',
            description: 'Directly access your data, join sources, and interrogate data however you want',
            layout: 'columns',
            features: [
                {
                    title: 'Add queries to notebooks',
                    description:
                        'Use PostHog notebooks to collect warehouse info, research topics, or just as a scratch pad',
                },
                {
                    title: 'Save queries as views',
                    description: 'Visualize your results as tables, graphs, and more, then save them for easy access',
                },
                {
                    title: 'Write SQL without knowing SQL',
                    description: 'Nobody likes writing SQL, except for PostHog AI',
                },
                {
                    title: 'Simplified syntax',
                    description: 'Access properties with dot notation like properties.$browser',
                },
                {
                    title: 'Smart joins',
                    description: 'Automatic joins between events, persons, and groups',
                },
                {
                    title: 'Product functions',
                    description: 'Built-in functions for cohorts, feature flags, and more',
                },
                {
                    title: 'ClickHouse power',
                    description: 'Full access to ClickHouse functions and performance',
                },
                {
                    title: 'Time zone handling',
                    description: 'Automatic time zone conversion for your project',
                },
            ],
            images: [
                {
                    src: 'https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/src/components/Product/DataWarehouse/images/stripe.png',
                    alt: '',
                },
            ],
        },
        {
            title: 'SQL insights',
            headline: 'Custom analytics beyond standard charts',
            description:
                'Build any analysis with SELECT, JOIN, WHERE, GROUP BY and visualize results in multiple formats.',
            images: [
                {
                    src: 'https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/contents/images/features/hogql/sql-light.png',
                    alt: 'SQL insights',
                },
            ],
            features: [
                {
                    title: 'Complex queries',
                    description: 'Multi-step CTEs, window functions, and subqueries',
                },
                {
                    title: 'Multiple visualizations',
                    description: 'Tables, line charts, bar charts, and more',
                },
                {
                    title: 'Save & share',
                    description: 'Save queries as insights to add to dashboards',
                },
                {
                    title: 'Export results',
                    description: 'Download query results as CSV or JSON',
                },
                {
                    title: 'Query templates',
                    description: 'Start from examples or save your own templates',
                },
            ],
        },
        {
            title: 'Hog',
            features: [
                {
                    title: 'SQL-compatible syntax',
                    headline: 'Familiar language for data teams',
                    description: (
                        <>
                            If you know SQL, you already know most of{' '}
                            <Link to="/docs/hog" state={{ newWindow: true }}>
                                Hog
                            </Link>
                            . Built to feel natural for data analysis.
                        </>
                    ),
                    images: [
                        {
                            src: 'https://res.cloudinary.com/dmukukwp6/image/upload/v1710055416/posthog.com/contents/images/docs/hog/hog-code-example.png',
                            alt: 'Hog code example',
                        },
                    ],
                    features: [
                        {
                            title: 'SQL expressions',
                            description: 'All SQL expressions work as Hog code',
                        },
                        {
                            title: 'Variables and functions',
                            description: 'Full programming language with functions, loops, and conditionals',
                        },
                        {
                            title: 'String templates',
                            description: 'F-string support for easy string formatting',
                        },
                        {
                            title: 'Pattern matching',
                            description: 'Regex and LIKE operators for string matching',
                        },
                        {
                            title: 'Type conversion',
                            description: 'Built-in functions for converting between types',
                        },
                    ],
                },
                {
                    title: 'Real-time destinations',
                    headline: 'Send data anywhere, instantly',
                    description: 'Build custom destinations that process and route your data in real-time.',
                    images: [
                        {
                            src: 'https://res.cloudinary.com/dmukukwp6/image/upload/custom_destination_input_f74eb7947d.png',
                            alt: 'Destination configuration',
                        },
                    ],
                    features: [
                        {
                            title: 'HTTP requests',
                            description: 'Send data to any webhook or API endpoint',
                        },
                        {
                            title: 'Async execution',
                            description: 'Pause and resume execution across fetch calls',
                        },
                        {
                            title: 'Error handling',
                            description: 'Gracefully handle failures with retries',
                        },
                        {
                            title: 'Input validation',
                            description: 'Define input schemas for reusable destinations',
                        },
                        {
                            title: 'Debug mode',
                            description: 'Print variables and trace execution flow',
                        },
                    ],
                },
                {
                    title: 'Secure execution',
                    headline: 'Run untrusted code safely',
                    description: 'The HogVM provides a secure sandbox for running user-provided code at scale.',
                    features: [
                        {
                            title: 'Bytecode VM',
                            description: 'Stack-based virtual machine with predictable behavior',
                        },
                        {
                            title: 'Resource limits',
                            description: 'Automatic timeouts and memory constraints',
                        },
                        {
                            title: 'No side effects',
                            description: 'Sandboxed execution prevents system access',
                        },
                        {
                            title: 'Serializable state',
                            description: 'Pause, serialize, and resume execution anywhere',
                        },
                        {
                            title: 'Multi-language support',
                            description: 'VMs in Python, TypeScript, and soon Rust',
                        },
                    ],
                },
            ],
        },
        {
            title: 'Data exploration',
            headline: 'Browse and query all available tables',
            description:
                'Explore events, persons, sessions, and external data sources with schema browser and auto-complete. Visualize your data warehouse data using tables, trends, and more. You can also use data warehouse data with insights.',
            images: [
                {
                    src: 'https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/contents/images/features/hogql/warehouse-light.png',
                    alt: 'Data exploration',
                },
            ],
            features: [
                {
                    title: 'Schema browser',
                    description: 'See all tables, columns, and data types',
                },
                {
                    title: 'Auto-complete',
                    description: 'IntelliSense for tables, columns, and functions',
                },
                {
                    title: 'Sample data',
                    description: 'Preview table contents before querying',
                },
                {
                    title: 'Query history',
                    description: 'Access and rerun previous queries',
                },
                {
                    title: 'Performance hints',
                    description: 'Optimization suggestions for slow queries',
                },
            ],
        },
        {
            title: 'External data sources',
            headline: 'Query PostHog alongside business data',
            description:
                'Join events with data from Stripe, HubSpot, databases, and more in a single query – so agents have all your context in one place to act on.',
            features: [
                {
                    title: 'Data warehouse',
                    description: 'Connect external sources like Stripe, HubSpot, and databases',
                },
                {
                    title: 'Unified queries',
                    description: 'JOIN PostHog events with external tables',
                },
                {
                    title: 'Custom views',
                    description:
                        'Save data as views to reuse in other queries. Materialize them to improve performance',
                },
                {
                    title: 'Scheduled syncs',
                    description: 'Keep external data fresh with automatic updates',
                },
                {
                    title: 'S3 & BigQuery',
                    description: 'Run SQL in PostHog over data in your object storage',
                },
            ],
        },
        {
            title: 'SQL API',
            headline: 'Programmatic access for automation',
            description: 'Query PostHog data from your applications, notebooks, or BI tools via API.',
            features: [
                {
                    title: 'REST API',
                    description: 'Simple HTTP endpoints for SQL queries',
                },
                {
                    title: 'Authentication',
                    description: 'Secure access with personal API keys',
                },
                {
                    title: 'Structured responses',
                    description: 'JSON results with types and metadata',
                },
                {
                    title: 'Python & JS SDKs',
                    description: 'Native libraries for common languages',
                },
                {
                    title: 'BI tool integration',
                    description: 'Connect Tableau, Looker, and other tools',
                },
            ],
        },
        {
            title: 'Variables',
            headline: 'Variables',
            description:
                'Use variables to customize queries for different users, customers, timeframes, and other filters.',
        },
        {
            title: 'Sync from anywhere',
            headline: 'Sync from anywhere',
            description: 'Stripe, Hubspot, Zendesk, Adwords, Salesforce, and major databases.',
            images: [
                {
                    src: 'https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/src/components/Product/DataWarehouse/images/custom.png',
                    alt: 'Sync from anywhere',
                },
            ],
        },
        {
            title: 'Person joins',
            headline: 'Person joins',
            description:
                'Join warehouse data on PostHog persons to easily access that data wherever you access the person profile.',
        },
        {
            title: 'Combine PostHog data with other sources',
            headline: 'Combine PostHog data with other sources',
            description:
                'Combine your product data with revenue, CRM, logs, and much more to get a full picture of your business.',
            images: [
                {
                    src: 'https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/src/components/Product/DataWarehouse/images/custom.png',
                    alt: 'Sync from anywhere',
                },
            ],
        },
        {
            title: 'Connectors',
            headline: 'Connectors',
            description:
                "Use Temporal to sync data from your sources into PostHog's S3 storage smoothly. We'll handle the data flow for you.",
            images: [
                {
                    src: 'https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/src/components/Product/DataWarehouse/images/custom.png',
                    alt: 'Sync from anywhere',
                },
            ],
        },
        // {
        //     title: 'Sync from Stripe',
        //     headline: 'Sync from Stripe',
        //     description: 'Combine financial and product data to see how usage turns into growth.',
        //     images: [
        //         {
        //             src: 'https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/src/components/Product/DataWarehouse/images/stripe.png',
        //             alt: 'Sync from Stripe',
        //         },
        //     ],
        // },
        // {
        //     title: 'Sync from Hubspot',
        //     headline: 'Sync from Hubspot',
        //     description: 'Bring your CRM into the mix and track your sales funnel directly in PostHog.',
        //     images: [
        //         {
        //             src: 'https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/src/components/Product/DataWarehouse/images/hubspot.png',
        //             alt: 'Sync from Hubspot',
        //         },
        //     ],
        // },
        // {
        //     title: 'Sync from Zendesk',
        //     headline: 'Sync from Zendesk',
        //     description: 'See how ticket volumes and SLA breaches impact sign-ups and churn.',
        //     images: [
        //         {
        //             src: 'https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/src/components/Product/DataWarehouse/images/zendesk.png',
        //             alt: 'Sync from Zendesk',
        //         },
        //     ],
        // },
    ],
    questions: [
        {
            question: 'How do specific user behaviors correlate to MRR?',
            url: '/tutorials/stripe-reports',
        },
        {
            question: 'How is user retention affected by SLA breaches in Zendesk?',
            url: '/tutorials/zendesk-reports',
        },
        {
            question: 'How are inbound leads using my product?',
            url: '/tutorials/hubspot-reports#usage-by-lead',
        },
        {
            question: 'How does predicted revenue compare to my actual revenue?',
            url: '/tutorials/hubspot-reports#getting-revenue-from-closed-deals-by-joining-with-stripe',
        },
        {
            question: 'How much revenue am I churning each month?',
            url: '/tutorials/stripe-reports#revenue-churn',
        },
        {
            question: 'Who are my power users, and how do they use my product?',
            url: '/tutorials/stripe-reports#usage-by-top-customers',
        },
    ],
    pairsWith: [
        {
            slug: 'product-analytics',
            description: 'Break down funnels, trends, and retention by plan, MRR, or deal stage',
        },
        {
            slug: 'experiments',
            description: 'Measure experiments on revenue, not just clicks',
        },
        {
            slug: 'data-pipelines',
            description: 'Send warehouse-enriched data to the rest of your stack',
        },
        {
            slug: 'endpoints',
            description: 'Turn any warehouse query into an API',
        },
    ],
    presenterNotes: {
        overview:
            '<strong>Presenter notes:</strong> Your product data is lonely inside your data warehouse unless it has all the relevant context you need to make decisions across your entire organization. PostHog\'s Data Warehouse brings them all together so you can answer questions like "What do high-value customers actually do in our product?" No ETL pipelines, no data engineering team required.',
        customers:
            "HeadshotPro analyzes Adwords alongside product usage - marketing and engineering finally speak the same language. Webshare syncs Intercom data to see how support interactions affect retention. These aren't special cases - everyone needs this. Even if you don't have a warehouse now, you'll need it eventually, so start small and ours will grow with you.",
        features:
            "<strong>More features:</strong><br /><br /><strong>Link multiple sources:</strong> Join Stripe customers to PostHog users. Connect everything with simple UI.<br /><br /><strong>Query with SQL:</strong> Full SQL access to everything. No vendor lock-in - it's just SQL.<br /><br /><strong>Save queries as views:</strong> Build once, use everywhere. Turn complex joins into simple tables.<br /><br /><strong>Custom sync periods:</strong> Daily for critical data, monthly for the rest. You control the schedule.<br /><br /><strong>Sync from Stripe:</strong> Connect revenue to product usage. See which features drive MRR. Track churn by usage patterns. Finally answer 'what makes customers pay more?'<br /><br /><strong>Sync from Hubspot:</strong> Link your CRM to product data. See how leads use your product before they buy. Track deal velocity by feature adoption.<br /><br /><strong>Sync from Zendesk:</strong> Connect support tickets to user behavior. Do angry users churn more? Which features cause the most tickets?<br /><br /><strong>Sync from anywhere:</strong> Google Ads, Salesforce, custom databases. If it has data, we can sync it. CSV uploads for everything else.",
        answers:
            'These are real questions our customers answer with PostHog. <em>MRR vs behavior?</em> Join Stripe and product data. <em>Support impact?</em> Connect Zendesk to retention metrics. <em>Lead quality?</em> Link HubSpot to usage patterns. <em>Revenue predictions?</em> Compare CRM forecasts to actual Stripe data. <em>Churn analysis</em> See payment failures alongside last product usage.',
        pricing:
            "1 million rows sync free every month, then it's simple per-row pricing. Compare that to Fivetran who charges thousands per month! We're 10x cheaper because we built for product teams, not enterprise IT. We grow with you vs. trying to lock you in with value-based pricing.",
        'comparison-summary':
            "Traditional data warehouses (Snowflake, BigQuery) require data teams. ETL tools (Fivetran, Stitch) just move data around. We do both – sync and analyze in one place. And because all your product data lives together, agents can query it and act on it – it's the context that makes your product self-driving. Perfect for product teams who need answers, not infrastructure.",
        'feature-comparison':
            "We don't have every connector Fivetran has. We don't need Snowflake's scale. What we have: dead-simple setup, built-in analytics, and it works with all your PostHog data. For most teams, that's exactly enough.",
        docs: "Step-by-step guides for each integration. Common query patterns documented. Real examples like 'revenue per feature' and 'support ticket impact on churn.' No data engineering degree required.",
        'pairs-with':
            "The magic happens when everything connects. Stripe revenue in your funnels. CRM data in your experiments. Support tickets next to session replays. This is why we built it - your data shouldn't live in silos.",
        'getting-started':
            "All you need to do get started is connect a source, authenticate, and pick the tables you want to sync. Data starts syncing immediately. Write your first JOIN query in 5 minutes. And if you're using Product Analytics, you can leverage customer usage data to answer questions that would have required stitching together and normalizing additional sources.",
    },
}
